import { useMutation } from '@apollo/react-hooks';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  AVAILABLE_DOCUMENT_TYPES_FOR_CREATION_QUERY,
  CREATE_DOCUMENT_MUTATION,
} from '../services/document-management.service';
import { AccountContext } from '../../../../components/AccountContext';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import { growlErrors } from '../../../../utils/NotificationUtil';
import FileDropzone from '../../../../components/file-dropzone/FileDropzone';
import { Button } from 'primereact/button';
import { DropdownComponent, FormikCalendar, InputTextComponent } from '../../../../components/prime-formik';
import apollo from '../../../../services/apollo';
import { computeEnforcedOnDate } from '../helpers/documents.helper';

const CreateDocument = () => {
  const [documentTypes, setDocumentTypes] = useState([]);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [createDocumentMutation] = useMutation(CREATE_DOCUMENT_MUTATION);

  useEffect(() => {
    apollo
      .query({
        query: AVAILABLE_DOCUMENT_TYPES_FOR_CREATION_QUERY,
      })
      .then((res) => {
        setDocumentTypes(res.data.availableDocumentTypesForCreation);
      });
  }, []);

  const createDocument = async ({ type, versionNumber, scheduledOn, file }) => {
    const scheduledOnInUTC = new Date(
      Date.UTC(scheduledOn.getFullYear(), scheduledOn.getMonth(), scheduledOn.getDate()),
    );

    try {
      await createDocumentMutation({
        variables: {
          input: {
            accountId: currentAccount,
            fieldName: '1',
            type,
            versionNumber,
            scheduledOn: scheduledOnInUTC,
          },
          file,
        },
      });

      history.push(`/accounts/${currentAccount}/documents`);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const initialValues = {
    type: null,
    versionNumber: '',
    scheduledOn: '',
    file: null,
  };

  const validationSchema = Yup.object().shape({
    type: Yup.string().required(),
    versionNumber: Yup.string().required(),
    scheduledOn: Yup.string().required(),
    file: Yup.mixed().required(),
  });

  const options = documentTypes.map((documentType) => {
    return {
      label: documentType,
      value: documentType,
    };
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header="Create document tasks">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => createDocument(values)}
              >
                {({ isValid, values: { type, scheduledOn }, isSubmitting, handleSubmit }) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      <div className="p-col-12">
                        <p>Select document type:</p>
                        <Field
                          id="type"
                          name="type"
                          component={DropdownComponent}
                          options={options}
                          filter
                          filterBy="label,value"
                        />

                        <p>Enter the document version number:</p>
                        <Field name="versionNumber" component={InputTextComponent} />

                        <p>Select the date when the document is scheduled on:</p>
                        <Field
                          name="scheduledOn"
                          id="scheduledOn"
                          component={FormikCalendar}
                          dateFormat="yy-M-dd"
                          minDate={new Date()}
                        />

                        {!!type && !!scheduledOn && (
                          <p>
                            Will be enforced on: <strong>{computeEnforcedOnDate(type, scheduledOn)}</strong>
                          </p>
                        )}

                        <p>Upload a new version of the document</p>
                        <FileDropzone />

                        <br />
                        <div className="align-center">
                          <Button type="submit" disabled={!isValid || isSubmitting} label="Upload" />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDocument;
